<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { fetchProposal, fetchListingById, createProposal, withdrawProposal, approveProposal, rejectProposal } from '@/services/api';
import MarkdownRenderer from '@/components/MarkdownRenderer.vue';
import { useStorage } from '@/composables/use-storage';
import { useToast } from '@/composables/use-toast';
import axios from '@/plugins/axios';
import SubmitConfirmationModal from '@/pages/user/proposal/submitConfirmationModal.vue';
import ConfirmWithdrawModal from '@/pages/user/proposal/confirmWithdrawModal.vue';
import ReviewProposalModal from '@/pages/user/proposal/reviewProposalModal.vue';

const router = useRouter();
const { addToast } = useToast();
const { userInfo } = useStorage();
const rfpSelected = ref(null);
const proposalId = ref(null);
const itemImage = ref(null);
const proposalMessage = ref('');
const isLoading = ref(false);

// Modals controls
const showSubmitProposalModal = ref(false);
const showConfirmWithdrawModal = ref(false);
const showReviewProposalModal = ref(false);

const proposalStatus = ref(null);
const isShowing = computed(() => {
    return router.currentRoute.value.query.proposalId;
})
const isReviewing = computed(() =>{
    return isShowing.value && rfpSelected.value?.applicant_id === userInfo.value.id;
});

const processImageFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
        addToast('Please select a valid image file', 'error');
        return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
        try {
            const result = await axios.post(`/api/users/${userInfo.value.id}/upload-image`, e.target.result);
            if ((result.status === 200 || result.status === 201) && result.data) {
                if (proposalMessage.value.includes('![Image](Uploading Image...)')) {
                    proposalMessage.value = proposalMessage.value.replace('![Image](Uploading Image...)', `![Image](${result.data})`);
                } else {
                    proposalMessage.value += `\n![Image](${result.data})`;
                }
                addToast('Image uploaded successfully', 'success');
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            addToast('Failed to upload image', 'error');
        }
    };
    reader.readAsDataURL(file);
};

const formatPrice = (price) => {
    const numPrice = parseFloat(price);
    return numPrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2, currency: 'USD', style: 'currency' });
}

const handlePaste = async (event) => {
    if (event.target?.files?.length > 0) {
        const file = event.target.files[0];
        proposalMessage.value += `\n\n![Image](Uploading Image...)`;
        await processImageFile(file);
        return;
    }

    if (!event.clipboardData) return;
    
    if (event.clipboardData.types && event.clipboardData.types.includes('text/plain')) {
        return;
    }
    
    const items = event.clipboardData?.items;
    if (!items) return;

    proposalMessage.value += `\n\n![Image](Uploading Image...)`;

    for (const item of items) {
        if (item.type.startsWith('image/')) {
            event.preventDefault();
            
            const file = item.getAsFile();
            if (file) {
                await processImageFile(file);
            }
            break;
        }
    }
};

const formatDate = (date) => {
    if(!date) return 'Does not expire';
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const handleOpenSubmitProposalModal = () => {
    if(!validateProposal()){
        return;
    }
    showSubmitProposalModal.value = true;
}

const validateProposal = () => {
    if(proposalMessage.value.length === 0){
        addToast('Please enter a proposal', 'error');
        return false;
    }
    return true;
}

const handleCreateProposal = async () => {
    isLoading.value = true;
    const values = {
        listing_id: rfpSelected.value.id,
        message: proposalMessage.value,
        price_lock_amount: rfpSelected.value.price,
        payout_address: '',
    }
    const response = await createProposal(values);
    isLoading.value = false;
    if(response){
        addToast('Proposal created successfully', 'success');
        router.replace(`/creator-profile?page=rfp-proposals-list`);
    }
}

const handleWithdrawProposal = async () => {
    isLoading.value = true;
    const response = await withdrawProposal(proposalId.value);
    isLoading.value = false;
    if(response){
        addToast('Proposal withdrawn successfully', 'success');
        router.replace(`/creator-profile?page=rfp-proposals-list`);
    }
}

const handleReviewProposal = async (action) => {
    const asctions = {
        approved: approveProposal,
        rejected: rejectProposal,
    }
    const response = await asctions[action](proposalId.value);
    if(response){
        addToast('Proposal ' + action + ' successfully', 'success');
        router.replace(`/creator-profile?page=rfp-proposals-list`);
    }
}

onMounted(async () => {
    isLoading.value = true;
    const queryParams = router.currentRoute.value.query;
    if(queryParams.proposalId){
        proposalId.value = queryParams.proposalId;
        const proposalResponse = await fetchProposal(queryParams.proposalId);
        if(proposalResponse){
            itemImage.value = proposalResponse.listing.preview_image;
            proposalMessage.value = proposalResponse.message;
            rfpSelected.value = proposalResponse.listing;
            proposalStatus.value = proposalResponse.status;
        }
    }
    
    if(queryParams.rfpId){
        rfpSelected.value = await fetchListingById(queryParams.rfpId);
        itemImage.value = rfpSelected.value.preview_image;
    }
    if(!proposalId.value && !queryParams.rfpId){
        router.replace(`/creator-profile?page=rfp-proposals-list`);
    }
    isLoading.value = false;
});


</script>
<template>
    <!-- Modals -->
    <SubmitConfirmationModal :show="showSubmitProposalModal" @isConfirmed="handleCreateProposal" @close="showSubmitProposalModal = false" />
    <ConfirmWithdrawModal :show="showConfirmWithdrawModal" @close="showConfirmWithdrawModal = false" @confirm="handleWithdrawProposal" />
    <ReviewProposalModal :show="showReviewProposalModal" @close="showReviewProposalModal = false" @review="handleReviewProposal" />
    
    <div>
        <router-link to="/creator-profile?page=rfp-proposals-list" class="flex underline items-center gap-1 text-white/80 hover:text-echoes-primary transition-colors duration-200 text-sm">
            <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd"
                    d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                    clip-rule="evenodd" />
            </svg>
            <span>Back to Proposals</span>
        </router-link>
    </div>

    <div class="bg-echoes-background-gray rounded-lg p-4 w-full mt-6">
        <div class="grid grid-cols-3">
            <div class="col-span-1 flex flex-col gap-2" v-if="!isLoading">
                <div>
                    <img :src="itemImage" alt="Dataset" class="w-1/2 object-cover rounded-lg">
                    <div class="text-lg mt-2 font-semibold">{{ rfpSelected?.name }}</div>
                </div>
                <div class="grid grid-cols-4 gap-2 font-semibold mt-2">
                    <div class="col-span-1 text-white/80">Issuer</div>
                    <div class="col-span-3">{{ rfpSelected?.applicant?.username }}</div>

                    <div class="col-span-1 text-white/80">Reward</div>
                    <div class="col-span-3">{{ formatPrice(rfpSelected?.price) }}</div>

                    <div class="col-span-1 text-white/80">Due by</div>
                    <div class="col-span-3">{{ formatDate(rfpSelected?.expire_at) }}</div>
                    
                </div>
            </div>
            <div v-else class="col-span-1 flex flex-col gap-2 animate-pulse">
                <div class="flex gap-2 flex-col">
                    <div class="w-1/2 h-48 bg-echoes-background-black rounded-lg"></div>
                    <div class="w-4/5 h-8 bg-echoes-background-black rounded-lg"></div>
                </div>
                <div class="grid grid-cols-4 gap-2 font-semibold">
                    <div class="col-span-1 text-white/80">Issuer</div>
                    <div class="col-span-3 bg-echoes-background-black rounded-lg w-1/3"></div>

                    <div class="col-span-1 text-white/80">Reward</div>
                    <div class="col-span-3 bg-echoes-background-black rounded-lg w-1/3"></div>

                    <div class="col-span-1 text-white/80">Due by</div>
                    <div class="col-span-3 bg-echoes-background-black rounded-lg w-1/3"></div>
                    
                </div>
            </div>
            <div class="col-span-2">
                <div class="text-center font-semibold">
                    Proposal Details
                </div>

                <div class="w-full max-h-[400px] overflow-y-auto">
                    <MarkdownRenderer 
                    v-model="proposalMessage" 
                    placeholder="How do you plan to respond to this dataset RFP? Include any relevant credentials or past experience working with data like this."
                    @paste="handlePaste"
                    :content-only="isShowing"
                    class="p-4" :class="{ 'bg-echoes-background-black rounded-lg': isShowing }"/>
                </div>
            </div>

            <div class="col-span-3 flex justify-end mt-4" v-if="proposalStatus === 'pending' || !isShowing">
                <button class="bg-echoes-primary text-white px-6 py-2 rounded-lg text-sm" @click="handleOpenSubmitProposalModal" v-if="!isShowing">Submit Proposal</button>
                <button class="bg-echoes-primary text-white px-6 py-2 rounded-lg text-sm" @click="showReviewProposalModal = true" v-if="isReviewing">Review Proposal</button>
                <button class="bg-echoes-primary text-white px-6 py-2 rounded-lg text-sm" @click="showConfirmWithdrawModal = true" v-if="!isReviewing && proposalId">Withdraw Proposal</button>
            </div>
        </div>
    </div>
</template>